import React from "react";
import LegalLayout from "./LegalLayout";

const UPDATED = "21 July 2026";

export default function Privacy() {
  return (
    <LegalLayout title="Privacy Policy" updated={UPDATED}>
      <Section title="1. Overview">
        <p>
          This Privacy Policy explains what personal data Leadmato ("we", "us") collects when
          you use leadmato.com or the Leadmato application ("Service"), how we use it, and the
          rights you have over it. We are committed to Indian data-protection law (DPDP Act
          2023) and to the GDPR where it applies.
        </p>
      </Section>

      <Section title="2. Data we collect">
        <ul>
          <li>
            <strong>Account data.</strong> Your name, email address, and profile picture from
            Google when you sign in.
          </li>
          <li>
            <strong>Workspace data.</strong> Your brand website, keywords, notes, saved leads,
            and tags.
          </li>
          <li>
            <strong>Usage data.</strong> Actions you perform (scans, saves) so we
            can enforce plan limits and improve the Service.
          </li>
          <li>
            <strong>Payment data.</strong> Transaction records (amount, currency, provider
            reference). Full card details are handled by Stripe / Razorpay - we never see or
            store them.
          </li>
          <li>
            <strong>Technical data.</strong> IP address, browser, device type, and cookies
            necessary for session management and fair-use enforcement.
          </li>
        </ul>
      </Section>

      <Section title="3. How we use your data">
        <ul>
          <li>To provide, secure and operate the Service.</li>
          <li>To enforce quota, fair-use and account safety limits.</li>
          <li>To process payments through Stripe and Razorpay.</li>
          <li>
            To generate scan results by calling third-party LLM providers
            (Anthropic, OpenAI, Gemini) - your inputs are transmitted to them under their
            respective privacy policies.
          </li>
          <li>To send transactional emails (receipt, security alerts, service updates).</li>
          <li>To respond to your support requests.</li>
        </ul>
      </Section>

      <Section title="4. Legal basis (GDPR-relevant users)">
        <p>
          We process personal data under one or more of these bases: (a) performance of the
          contract with you; (b) our legitimate interest in operating a safe, reliable Service;
          (c) your consent (for marketing emails, which you can withdraw anytime); (d) legal
          obligation (e.g. tax records).
        </p>
      </Section>

      <Section title="5. Sharing your data">
        <p>We share personal data only with:</p>
        <ul>
          <li>
            <strong>Sub-processors:</strong> Google Cloud (hosting), MongoDB Atlas (database),
            Stripe & Razorpay (payments), Anthropic/OpenAI/Gemini (LLM providers), Jina AI (data
            retrieval), Resend/SendGrid (transactional email).
          </li>
          <li>Law enforcement and courts when compelled by valid legal process.</li>
          <li>An acquirer or successor entity in the event of a merger or sale.</li>
        </ul>
        <p>We never sell your personal data.</p>
      </Section>

      <Section title="6. Cookies">
        <p>
          We use an essential session cookie (<code>session_token</code>) to keep you logged in. It
          is HttpOnly, Secure, SameSite=None, and scoped to leadmato.com. We also run the Meta
          (Facebook) Pixel to measure sign-ups and support advertising, which may set the
          <code> _fbp</code> cookie. See our <a href="/cookie-policy">Cookie Policy</a> for the full
          list and how to opt out.
        </p>
      </Section>

      <Section title="7. Data retention">
        <ul>
          <li>Account data - while your account is active, plus 30 days after deletion.</li>
          <li>Scan results & saved leads - up to 12 months, then archived or purged.</li>
          <li>Payment records - retained for 7 years per Indian tax and accounting law.</li>
          <li>Server logs - 30 days.</li>
        </ul>
      </Section>

      <Section title="8. Your rights">
        <p>
          Regardless of jurisdiction, you can request:
        </p>
        <ul>
          <li>A copy of the personal data we hold about you.</li>
          <li>Correction of inaccurate data.</li>
          <li>Deletion of your account and associated data.</li>
          <li>Export of your saved leads in JSON or CSV.</li>
          <li>Withdrawal of consent for marketing communications.</li>
        </ul>
        <p>
          Email{" "}
          <a href="mailto:support@aifreedomclub.com" className="text-lm-primary underline underline-offset-4">
            support@aifreedomclub.com
          </a>{" "}
          and we will respond within 30 days.
        </p>
      </Section>

      <Section title="9. Security">
        <p>
          All traffic is served over HTTPS. Sessions are token-based with HttpOnly cookies.
          Passwords are not used (Google-only auth). Databases are access-controlled and
          regularly backed up. We take reasonable measures but no online service can guarantee
          absolute security.
        </p>
      </Section>

      <Section title="10. International transfers">
        <p>
          Some sub-processors host or process data outside India (e.g. LLM providers in the
          United States). Where required by law, we rely on Standard Contractual Clauses or
          equivalent safeguards.
        </p>
      </Section>

      <Section title="11. Children">
        <p>Leadmato is not directed at children under 18. We do not knowingly collect data from minors.</p>
      </Section>

      <Section title="12. Changes to this Policy">
        <p>
          We will notify you of material changes via in-app banner or email. Continued use of
          the Service after the effective date constitutes acceptance.
        </p>
      </Section>

      <Section title="13. Contact us">
        <p>
          Data Protection queries:{" "}
          <a href="mailto:support@aifreedomclub.com" className="text-lm-primary underline underline-offset-4">
            support@aifreedomclub.com
          </a>
          <br />
          General support:{" "}
          <a href="mailto:support@aifreedomclub.com" className="text-lm-primary underline underline-offset-4">
            support@aifreedomclub.com
          </a>
        </p>
      </Section>
    </LegalLayout>
  );
}

function Section({ title, children }) {
  return (
    <section className="mb-8">
      <h2 className="font-display font-semibold text-xl md:text-2xl tracking-tight text-lm-text mb-3">
        {title}
      </h2>
      <div className="text-lm-text2 space-y-3 [&_ul]:list-disc [&_ul]:pl-6 [&_ul]:space-y-2 [&_a]:text-lm-primary [&_a]:underline [&_a]:underline-offset-4">
        {children}
      </div>
    </section>
  );
}
